/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactNode, useRef } from 'react';

import { css as customCss } from '@compiled/react';

import { cssMap, cx, jsx } from '@atlaskit/css';
/* eslint-disable @repo/internal/react/require-jsdoc */
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token, useThemeObserver } from '@atlaskit/tokens';

import { BrandHeading } from '../brand-heading';
import { HeroGridBackground } from '../hero/grid-background';
import { Image } from '../image';
import { PageContentContainer } from '../page-content-container';
import { RelativePosition } from '../position';

const styles = cssMap({
	bannerContainer: {
		display: 'flex',
		width: '100%',
		justifyContent: 'center',
	},
	backgroundColor: {
		backgroundColor: token('elevation.surface.sunken'),
	},
	bannerWidth: {
		width: '100%',
		maxWidth: '980px',
		flex: 1,
	},
	contentContainer: {
		height: '100%',
		minHeight: '380px',
		flex: 1,
		paddingBlock: token('space.1000'),
	},
	imageContainer: {
		flex: 1,
	},
	edgeToEdgeWrapper: {
		position: 'absolute',
		zIndex: -1,
		insetBlockStart: 0,
		insetBlockEnd: 0,
		insetInlineStart: '50%',
		width: '100vw',
		pointerEvents: 'none',
	},
});

const customEdgeToEdgeWrapperStyles = customCss({
	insetInlineEnd: 'auto',
	marginInlineStart: '-50vw',
});

export const Banner = ({
	title,
	description,
	image,
	hasBackgroundColor,
	hasDotGrid = false,
}: {
	title: string;
	description: string;
	image?: {
		light: string;
		dark: string;
	};
	hasBackgroundColor?: boolean;
	hasDotGrid?: boolean;
}): React.JSX.Element => {
	const { colorMode = 'light' } = useThemeObserver();
	const src = colorMode === 'dark' ? image?.dark || '' : image?.light || '';
	const containerRef = useRef<HTMLDivElement>(null);

	const content = (
		<Inline alignBlock="center" xcss={styles.bannerWidth}>
			<Inline xcss={styles.contentContainer} alignBlock="center" spread="space-between">
				<Stack space="space.100">
					<BrandHeading as="h1" size="xxlarge">
						{title}
					</BrandHeading>
					<Text size="large">{description}</Text>
				</Stack>
			</Inline>
			<ImageContainer>
				{image ? <Image src={src} alt={`Banner for ${title} page`} /> : <div></div>}
			</ImageContainer>
		</Inline>
	);

	return (
		<Box xcss={cx(styles.bannerContainer, hasBackgroundColor && styles.backgroundColor)}>
			{hasDotGrid ? (
				<RelativePosition>
					<div ref={containerRef} css={[styles.edgeToEdgeWrapper, customEdgeToEdgeWrapperStyles]}>
						<HeroGridBackground containerRef={containerRef} />
					</div>
					<PageContentContainer>{content}</PageContentContainer>
				</RelativePosition>
			) : (
				<PageContentContainer>{content}</PageContentContainer>
			)}
		</Box>
	);
};

const ImageContainer = ({ children }: { children: ReactNode }) => {
	return <Box xcss={styles.imageContainer}>{children}</Box>;
};
